import { ChevronRight } from "lucide-react";
import { trpc } from "~/api/trpc";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import { useLanguageStore } from "~/stores/language-store";
import { isEmbedded } from "~/lib/embed";

export const TREATMENT_PLANNER_IMAGE_URL = 'https://samson-public-assets.s3.eu-central-1.amazonaws.com/booking-funnel/splash-art-kalia-waves.jpg';

interface TreatmentPlannerCardProps {
    title?: string;
    subtitle?: string;
}

const TreatmentPlannerCard = ({ title, subtitle }: TreatmentPlannerCardProps) => {
    const { t } = useLanguageStore();
    const { token, isAuthenticated } = useCustomerAuthStore();

    // Only fetch if authenticated
    const { data: hasTreatmentPlan, isLoading } = trpc.publicCustomer.hasTreatmentPlan.useQuery(
        undefined,
        {
            enabled: isAuthenticated(),
            staleTime: 60000, // 1 minute cache
            retry: false, // Don't retry on error
        }
    );

    // Don't render if not authenticated or no treatment plan
    if (!isAuthenticated() || isLoading || !hasTreatmentPlan) {
        return null;
    }

    const handleClick = () => {
        if (!token) {
            return;
        }

        // Determine environment
        const isProd = import.meta.env.VITE_APP_ENV === 'prod' || import.meta.env.PROD;
        const portalBaseUrl = isProd 
            ? 'https://portal.kalialab.de' 
            : 'https://staging.portal.kalialab.de';

        const portalUrl = `${portalBaseUrl}/auth/login?token=${token}`;
        if (isEmbedded()) {
            window.open(portalUrl, "_blank");
            return;
        }

        // Redirect to portal with auth token
        window.location.href = portalUrl;
    };

    return (
        <div
            className="relative w-full rounded-lg cursor-pointer overflow-hidden group"
            style={{
                minHeight: '100px',
                backgroundImage: `url('${TREATMENT_PLANNER_IMAGE_URL}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
            onClick={handleClick}
        >
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
            
            {/* Content */}
            <div className="relative flex items-center justify-between px-5 py-6">
                <div className="flex flex-col gap-0.5">
                    <h3 className="text-white text-lg font-semibold drop-shadow-md">
                        {title ?? t('yourTreatmentPlan')}
                    </h3>
                    <p className="text-white/90 text-sm drop-shadow-md">
                        {subtitle ?? t('viewYourPersonalizedPlan')}
                    </p>
                </div>
                <ChevronRight className="w-6 h-6 text-white drop-shadow-md group-hover:translate-x-1 transition-transform" />
            </div>
        </div>
    );
};

export default TreatmentPlannerCard;
